/**
 * The ring on the landing page.
 *
 * Not a picture and not a special mode: the real `Scene` and the real
 * `InputController` over `data/sample.ts`, so spin, wheel zoom, pinch, hover
 * and cluster-click behave exactly as they do inside a calendar. What is
 * missing is everything that writes — no `PinStore`, no API client, no URL
 * hash — so a visitor can move the instrument about and nothing anywhere
 * remembers it.
 */
import type { CalendarBundle, ViewLayout, ViewState } from '@circle/shared';
import { Scene, type SceneEntry } from '../../core/scene';
import { InputController, computeFit } from '../../core/input';
import { prefersReducedMotion } from '../../core/motion';
import { sampleBundle } from '../../data/sample';
import { Tooltip, describeEntry } from '../tooltip';

export { prefersReducedMotion };

/**
 * The ring arrives a few degrees off January and settles. One orchestrated
 * moment on load; after that nothing moves unless a person moves it.
 */
export const INTRO_ROTATION = -0.38;

export interface DemoRingOptions {
  host: HTMLElement;
  bundle?: CalendarBundle;
  today?: Date;
  /** The settling spin. Off under `prefers-reduced-motion` whatever this says. */
  intro?: boolean;
  /** Which layout of the sample circles to open on. */
  layout?: ViewLayout;
}

export class DemoRing {
  readonly scene: Scene;
  readonly input: InputController;
  private readonly host: HTMLElement;
  private readonly tooltip: Tooltip;
  private readonly bundle: CalendarBundle;
  private readonly today: Date;
  private size: { width: number; height: number };

  constructor(options: DemoRingOptions) {
    this.host = options.host;
    this.bundle = options.bundle ?? sampleBundle;
    this.today = options.today ?? new Date();
    this.size = this.measure();

    this.scene = new Scene(this.host);
    this.tooltip = new Tooltip(this.host);

    const settle = options.intro !== false && !prefersReducedMotion();
    const view: ViewState = {
      year: this.today.getFullYear(),
      rotation: settle ? INTRO_ROTATION : 0,
      ...computeFit(this.size.width, this.size.height),
    };

    this.scene.resize(this.size.width, this.size.height);
    if (options.layout) this.scene.setViewLayout(options.layout);
    this.scene.render(this.bundle, view, this.today);

    this.input = new InputController({
      scene: this.scene,
      initial: view,
      // Nowhere to persist a view to: the landing page owns no URL hash.
      onView: () => {},
      onHover: (entry, screen) => this.tooltip.show(entry ? this.describe(entry) : '', screen),
      // A finger never hovers. Tapping a mark is the same question hovering
      // asks, so it gets the same answer, placed on the thing that was tapped.
      onSelect: (entry) => {
        if (entry) this.tooltip.showOn(this.describe(entry), entry);
        else this.tooltip.hide();
      },
    });
    this.input.setViewport(this.size.width, this.size.height);
    this.input.setView(view, false);
    this.input.settle();

    if (settle) this.input.reset(true);

    window.addEventListener('resize', this.onResize);
  }

  destroy(): void {
    window.removeEventListener('resize', this.onResize);
    this.input.destroy();
    this.tooltip.destroy();
    this.scene.destroy();
  }

  private measure(): { width: number; height: number } {
    return {
      width: this.host.clientWidth || this.host.offsetWidth || 960,
      height: this.host.clientHeight || this.host.offsetHeight || 600,
    };
  }

  /** Keep the ring fitted, and keep it centred on whatever the box became. */
  private readonly onResize = (): void => {
    const next = this.measure();
    if (next.width === this.size.width && next.height === this.size.height) return;
    this.size = next;
    this.scene.resize(next.width, next.height);
    this.input.setViewport(next.width, next.height);
    this.input.reset(false);
  };

  /**
   * The big bang, on the front page. Whether it is a bang or a cut is the
   * scene's and the controller's call — both ask `prefersReducedMotion` — so
   * this only asks for the animation. The sample "Split" view sets a
   * `placement` on each top-level circle; the layout arrives here whole.
   */
  setViewLayout(layout: ViewLayout): void {
    this.scene.setViewLayout(layout, { animate: true });
    // The rings have moved, so what fits has changed with them.
    this.input.reset(true);
  }

  /** The same words the app uses, so the demo tells no story of its own. */
  describe(entry: SceneEntry): string {
    return describeEntry(entry, { scene: this.scene, year: this.input.currentView.year, today: this.today });
  }
}

/**
 * Mount after the first frame, so the shell paints before a year of day ticks
 * is built. The box the ring lands in is sized in CSS and is already on the
 * page, so nothing moves when it arrives.
 */
export interface DemoRingHandle {
  destroy: () => void;
  /** Ignored until the ring exists; the layout asked for is the one it opens on. */
  setViewLayout: (layout: ViewLayout) => void;
}

export function mountDemoRing(
  host: HTMLElement,
  options: Omit<DemoRingOptions, 'host'> = {},
): DemoRingHandle {
  let ring: DemoRing | null = null;
  let cancelled = false;
  let wanted = options.layout;
  const raf =
    typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame(() => {
          if (cancelled) return;
          ring = new DemoRing({ host, ...options, ...(wanted ? { layout: wanted } : {}) });
        })
      : null;
  return {
    setViewLayout(layout) {
      wanted = layout;
      ring?.setViewLayout(layout);
    },
    destroy() {
      cancelled = true;
      if (raf !== null && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf);
      ring?.destroy();
      ring = null;
    },
  };
}
